import type { DoorTypeId } from '@planner/config';
import { doorTypes, doorTypeValueStrip } from '../../appdata.js';
import { DoorTypeCard } from '../components/DoorTypeCard.js';
import { StepNavigation, StepProgress } from '../components/StepProgress.js';
import { fromPrices, useProducts } from '../lib/catalogue.js';
import { formatPrice } from '../lib/format.js';
import { useDoorStore } from '../stores/doorStore.js';
import { track } from '../lib/analytics/index.js';

const valueStripIcons: Record<(typeof doorTypeValueStrip)[number]['id'], string> = {
  'made-to-order': 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  'frame-colours':
    'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01',
  'premium-aluminium':
    'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
};

export function DoorTypeStep() {
  const doorType = useDoorStore((state) => state.draft.doorType);
  const selectDoorType = useDoorStore((state) => state.selectDoorType);
  const products = useProducts();
  const prices = products.data ? fromPrices(products.data) : undefined;

  // Prices are informative only: no state here disables choosing a door.
  const priceStatus = (id: DoorTypeId) => {
    if (products.isPending) {
      return <p className="text-gray-500">Loading price…</p>;
    }
    const price = prices?.[id];
    if (price !== undefined) {
      return <p className="font-bold text-brand">from {formatPrice(price)}</p>;
    }
    return (
      <>
        <p className="text-gray-600">Price unavailable</p>
        {/* A successful response with no usable price may be fixed by a later sync. */}
        <button
          type="button"
          data-name={`door-type-price-retry-${id}`}
          onClick={() => void products.refetch()}
          className="pointer-events-auto text-xs font-semibold text-brand-strong underline hover:no-underline focus-visible:outline-2 focus-visible:outline-brand"
        >
          Retry
        </button>
      </>
    );
  };

  return (
    <>
      <StepProgress currentIndex={0} />
      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-8 sm:py-10">
        <h1 id="door-type-heading" className="text-2xl font-semibold sm:text-3xl">
          What type of screen door do you need?
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          These hinged doors are custom-made to your measurements in Australia. Select a type to
          continue.
        </p>

        <div
          role="radiogroup"
          aria-labelledby="door-type-heading"
          className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5"
        >
          {doorTypes.map((door) => (
            <DoorTypeCard
              key={door.id}
              door={door}
              selected={door.id === doorType}
              price={priceStatus(door.id)}
              onSelect={() => {
                if (doorType !== door.id) {
                  selectDoorType(door.id);
                  track('configuration_changed', { field: 'door_type', value: door.id });
                }
              }}
            />
          ))}
        </div>

        <ul
          data-name="door-type-value-strip"
          className="mt-8 grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 sm:grid-cols-3"
        >
          {doorTypeValueStrip.map((item) => (
            <li key={item.id} className="flex items-center gap-2.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
                <svg
                  aria-hidden="true"
                  className="size-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={valueStripIcons[item.id]}
                  />
                </svg>
              </span>
              <span className="min-w-0 text-xs leading-tight">
                <span className="block font-semibold">{item.title}</span>
                <span className="block text-gray-600">{item.body}</span>
              </span>
            </li>
          ))}
        </ul>

        <StepNavigation currentIndex={0} canContinue={doorType !== null} />
      </main>
    </>
  );
}
